import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [step, setStep] = useState<'email' | 'codigo'>('email');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  // Solicitar código
  const solicitarCodigoMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await axiosInstance.post(`/auth/solicitar-codigo`, { email });
      return response.data;
    },
    onSuccess: () => {
      setError('');
      setStep('codigo');
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Error al solicitar el código');
    }
  });

  // Verificar código
  const verificarCodigoMutation = useMutation({
    mutationFn: async ({ email, codigo }: { email: string; codigo: string }) => {
      const response = await axiosInstance.post(`/auth/verificar-codigo`, { email, codigo });
      return response.data;
    },
    onSuccess: (data) => {
      console.log('Login successful:', data);
      console.log('Token:', data.access_token);
      console.log('User:', data.user);
      
      try {
        console.log('Calling login function...');
        login(data.user, data.access_token);
        
        // Verificar que se guardó en localStorage
        console.log('Token in localStorage:', localStorage.getItem('token'));
        console.log('User in localStorage:', localStorage.getItem('user'));
        
        console.log('About to navigate...');
        console.log('User role:', data.user.rol);
        
        // Esperar un momento antes de navegar
        setTimeout(() => {
          console.log('Navigating now...');
          if (data.user.rol === 'admin' || data.user.rol === 'jtp' || data.user.rol === 'ayudante') {
            console.log('Navigating to /admin');
            navigate('/admin');
          } else if (data.user.rol === 'profesor') {
            console.log('Navigating to /profesor');
            navigate('/profesor');
          } else {
            console.error('Unknown role:', data.user.rol);
            navigate('/login');
          }
        }, 100);
      } catch (error) {
        console.error('Error in login process:', error);
      }
    },
    onError: (error: any) => {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      setError(error.response?.data?.message || 'Error al verificar el código');
    }
  });

  const handleSolicitarCodigo = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      solicitarCodigoMutation.mutate(email.trim());
    }
  };

  const handleVerificarCodigo = (e: React.FormEvent) => {
    e.preventDefault();
    if (codigo.trim()) {
      verificarCodigoMutation.mutate({ email: email.trim(), codigo: codigo.trim() });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-md mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Iniciar Sesión
            </h1>
            <p className="text-gray-600">
              Portal Académico - Facultad de Informática
            </p>
          </div>

          {/* Paso 1: Ingresar email */}
          {step === 'email' && (
            <form onSubmit={handleSolicitarCodigo} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Institucional
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="tu.email@unlp.edu.ar"
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={solicitarCodigoMutation.isPending}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {solicitarCodigoMutation.isPending ? 'Enviando...' : 'Solicitar Código'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="text-blue-600 hover:text-blue-700 text-sm"
                >
                  Volver al inicio
                </button>
              </div>
            </form>
          )}

          {/* Paso 2: Ingresar código */}
          {step === 'codigo' && (
            <form onSubmit={handleVerificarCodigo} className="space-y-4">
              <div className="text-center mb-4">
                <p className="text-gray-600">
                  Se ha enviado un código de acceso a:
                </p>
                <p className="font-medium text-gray-900">{email}</p>
              </div>

              <div>
                <label htmlFor="codigo" className="block text-sm font-medium text-gray-700 mb-2">
                  Código de Acceso
                </label>
                <input
                  type="text"
                  id="codigo"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-center text-lg font-mono"
                  placeholder="000000"
                  maxLength={6}
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={verificarCodigoMutation.isPending}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {verificarCodigoMutation.isPending ? 'Verificando...' : 'Verificar Código'}
              </button>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setStep('email')}
                  className="text-gray-600 hover:text-gray-700 text-sm"
                >
                  Cambiar email
                </button>
                <button
                  type="button"
                  onClick={() => solicitarCodigoMutation.mutate(email)}
                  disabled={solicitarCodigoMutation.isPending}
                  className="text-blue-600 hover:text-blue-700 text-sm disabled:opacity-50"
                >
                  Reenviar código
                </button>
              </div>
            </form>
          )}

          {/* Información adicional */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">¿Necesitas ayuda?</h3>
              <p className="text-sm text-blue-700">
                Si no recibes el código de acceso, verifica que tu email esté correcto o contacta al administrador del sistema.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
